"use client"

import { useState, useEffect, useRef } from "react"
import {
  Box,
  Grid,
  Typography,
  Paper,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Tooltip,
  useTheme,
  CircularProgress,
} from "@mui/material"
import {
  Add as AddIcon,
  Visibility as VisibilityIcon,
  AccountBalance as CreditsIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
} from "@mui/icons-material"
import { useNavigate } from "react-router-dom"
import { useAuth } from "../../contexts/AuthContext"
import { useNotification } from "../../contexts/NotificationContext"
import PageHeader from "../../components/common/PageHeader"
import LoadingSpinner from "../../components/common/LoadingSpinner"
import EmptyState from "../../components/common/EmptyState"
import ErrorDisplay from "../../components/common/ErrorDisplay"
import { getCustomers } from "../../services/customer.service"
import { getCredits, addCredits, getCreditsByOrganization, getCreditsByOrganizationPaginated } from "../../services/credit.service"
import InfiniteScroll from 'react-infinite-scroll-component';
import { motion, AnimatePresence } from "framer-motion"
import StatusBadge from "../../components/common/StatusBadge"
import LoadingButton from "../../components/common/LoadingButton"

const Credits = () => {
  const { selectedOrganization, user } = useAuth()
  const { showNotification } = useNotification()
  const navigate = useNavigate()
  const theme = useTheme()

  const [customers, setCustomers] = useState([])
  const [credits, setCredits] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const [totalCount, setTotalCount] = useState(0)
  const limit = 20
  const [isFetching, setIsFetching] = useState(false)
  const [noMoreData, setNoMoreData] = useState(false)
  const [showLoadMore, setShowLoadMore] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const searchTimeoutRef = useRef(null)
  const [openAddCreditsDialog, setOpenAddCreditsDialog] = useState(false)
  const [selectedCustomer, setSelectedCustomer] = useState(null)
  const [creditsToAdd, setCreditsToAdd] = useState("")
  const [addingCredits, setAddingCredits] = useState(false)
  const [totalCredits, setTotalCredits] = useState(0)

  useEffect(() => {
    if (selectedOrganization) {
      setCredits([]);
      setPage(1);
      setHasMore(true);
      fetchCreditsPaginated(1, true);
    } else if (user?.organizationId) {
      // Fallback to user's organization if selectedOrganization is null
      setCredits([]);
      setPage(1);
      setHasMore(true);
      fetchCreditsWithOrgIdPaginated(user.organizationId, 1, true);
    } else {
      // Set loading to false if we can't fetch data
      setLoading(false)
      setError("No organization selected. Please select an organization to view credits.")
    }
  }, [selectedOrganization, user])

  const fetchCreditsPaginated = async (pageToFetch = 1, reset = false, query = "") => {
    setIsFetching(true);
    setLoading(true);
    try {
      const params = { organizationId: selectedOrganization._id, page: pageToFetch, limit};
      if(query) {
        params.search = query;
      }
      const response = await getCreditsByOrganizationPaginated(params);
      let newCredits = response.data.map((credit) => ({ id: credit._id, ...credit }));
      setTotalCount(response.count || 0);
      
      if (reset) {
        setCredits(newCredits);
        setNoMoreData(false);
        setShowLoadMore(false);
        // Calculate total credits for the first page
        const total = newCredits.reduce((sum, customer) => sum + (customer.credits || 0), 0)
        setTotalCredits(total)
      } else {
        setCredits((prev) => {
          const existingIds = prev.map((credit) => credit.id);
          newCredits = newCredits.filter((credit) => !existingIds.includes(credit.id));
          return [...prev, ...newCredits];
        });
      }
      
      if (newCredits.length < limit) {
        setNoMoreData(true);
        setShowLoadMore(true);
      } else {
        setNoMoreData(false);
        setShowLoadMore(false);
      }
      setHasMore(!showLoadMore);
    } catch (error) {
      setError(error.message || "Failed to load credits data");
    } finally {
      setLoading(false);
      setIsFetching(false);
    }
  };

  const fetchCreditsWithOrgIdPaginated = async (orgId, pageToFetch = 1, reset = false, query = "") => {
    setIsFetching(true);
    setLoading(true);
    try {
      const params = { organizationId: orgId, page: pageToFetch, limit};
      if(query) {
        params.search = query;
      }
      const response = await getCreditsByOrganizationPaginated(params);
      let newCredits = response.data.map((credit) => ({ id: credit._id, ...credit }));
      setTotalCount(response.count || 0);
      
      if (reset) {
        setCredits(newCredits);
        setNoMoreData(false);
        setShowLoadMore(false);
        // Calculate total credits for the first page
        const total = newCredits.reduce((sum, customer) => sum + (customer.credits || 0), 0)
        setTotalCredits(total)
      } else {
        setCredits((prev) => {
          const existingIds = prev.map((credit) => credit.id);
          newCredits = newCredits.filter((credit) => !existingIds.includes(credit.id));
          return [...prev, ...newCredits];
        });
      }
      
      if (newCredits.length < limit) {
        setNoMoreData(true);
        setShowLoadMore(true);
      } else {
        setNoMoreData(false);
        setShowLoadMore(false);
      }
      setHasMore(!showLoadMore);
    } catch (error) {
      setError(error.message || "Failed to load credits data");
    } finally {
      setLoading(false);
      setIsFetching(false);
    }
  };

  const fetchNextPage = () => {
    if (isFetching || showLoadMore) return;
    setPage((prevPage) => {
      let nextPage = prevPage;
      if(totalCount >= limit) {
        nextPage = prevPage + 1;
      }
      if (selectedOrganization) {
        fetchCreditsPaginated(nextPage, false, searchTerm);
      } else if (user?.organizationId) {
        fetchCreditsWithOrgIdPaginated(user.organizationId, nextPage, false, searchTerm);
      }
      return nextPage;
    });
  };

  const handleLoadMore = () => {
    setShowLoadMore(false);
    setPage((prevPage) => {
      let nextPage = prevPage;
      if(totalCount >= limit) {
        nextPage = prevPage + 1;
      }
      if (selectedOrganization) {
        fetchCreditsPaginated(nextPage, false, searchTerm);
      } else if (user?.organizationId) {
        fetchCreditsWithOrgIdPaginated(user.organizationId, nextPage, false, searchTerm);
      }
      return nextPage;
    });
  };

  const handleSearchChange = (event) => {
    const query = event.target.value;
    setSearchTerm(query);

    clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setPage(1);
      setCredits([]);
      setNoMoreData(false);
      setShowLoadMore(false);

      if (selectedOrganization) {
        fetchCreditsPaginated(1, true, query);
      } else if (user?.organizationId) {
        fetchCreditsWithOrgIdPaginated(user.organizationId, 1, true, query);
      }
    }, 500);
  }

  const handleOpenAddCreditsDialog = (customer) => {
    setSelectedCustomer(customer)
    setCreditsToAdd("")
    setOpenAddCreditsDialog(true)
  }

  const handleCloseAddCreditsDialog = () => {
    setOpenAddCreditsDialog(false)
    setSelectedCustomer(null)
    setCreditsToAdd("")
  }

  // Update the handleAddCredits function to handle subscription check error
  const handleAddCredits = async () => {
    if (!selectedCustomer || !creditsToAdd || isNaN(creditsToAdd) || Number(creditsToAdd) <= 0) {
      showNotification("Please enter a valid number of credits", "error")
      return
    }

    const orgId = selectedOrganization?._id || user?.organizationId
    if (!orgId) {
      showNotification("No organization selected", "error")
      return
    }

    setAddingCredits(true)
    try {
      // Fix: Pass parameters directly instead of as a nested object
      await addCredits(
        selectedCustomer._id,
        Number(creditsToAdd),
        "manual", // type
        `Credits added by ${user?.name || "admin"}`, // description
        orgId,
      )
      showNotification(`Successfully added ${creditsToAdd} credits to ${selectedCustomer.name}`, "success")
      handleCloseAddCreditsDialog()
      // Refresh data
      if (selectedOrganization) {
        fetchCreditsPaginated(1, true, searchTerm);
      } else if (user?.organizationId) {
        fetchCreditsWithOrgIdPaginated(user.organizationId, 1, true, searchTerm);
      }
    } catch (err) {
      console.error("Failed to add credits:", err)
      // Update error message to handle subscription check
      if (err.response?.data?.message?.includes("active subscription")) {
        showNotification("Cannot add credits: Customer does not have an active subscription", "error")
      } else {
        showNotification(err.response?.data?.message || "Failed to add credits", "error")
      }
    } finally {
      setAddingCredits(false)
    }
  }

  const handleViewCustomerCredits = (customerId) => {
    navigate(`/credits/customer/${customerId}`)
  }

  // Note: Search is now handled server-side, no need for client-side filtering

  if (error) {
    return <ErrorDisplay message="There was a problem loading the credits data." details={error} onRetry={() => {
      if (selectedOrganization) {
        fetchCreditsPaginated(1, true, searchTerm);
      } else if (user?.organizationId) {
        fetchCreditsWithOrgIdPaginated(user.organizationId, 1, true, searchTerm);
      }
    }} />
  }

  return (
    <Box>
      <PageHeader
        title="Credits Management"
        subtitle="Manage credits for your customers"
        breadcrumbs={[{ text: "Credits" }]}
        action={true}
        actionText="Add Credits"
        actionIcon={<AddIcon />}
        onActionClick={() => navigate("/customers")}
      />

      {/* Credits Overview */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Paper
            component={motion.div}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            sx={{
              p: 3,
              borderRadius: 3,
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
              border: "1px solid rgba(0, 0, 0, 0.05)",
              background: "linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(249, 250, 251, 0.95) 100%)",
              height: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                backgroundColor: "rgba(99, 102, 241, 0.1)",
                p: 2,
                borderRadius: "50%",
                mb: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CreditsIcon sx={{ fontSize: 40, color: "primary.main" }} />
            </Box>
            <Typography variant="h3" fontWeight="bold" gutterBottom>
              {totalCredits.toLocaleString()}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Total Available Credits
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={8}>
          <Paper
            component={motion.div}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            sx={{
              p: 3,
              borderRadius: 3,
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
              border: "1px solid rgba(0, 0, 0, 0.05)",
              background: "linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(249, 250, 251, 0.95) 100%)",
              height: "100%",
            }}
          >
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Credits Distribution
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", mt: 2 }}>
              {credits.slice(0, 5).map((customer, index) => (
                <Tooltip key={customer._id} title={`${customer.name}: ${customer.credits} credits`}>
                  <Box
                    component={motion.div}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                    sx={{
                      width: `${(customer.credits / totalCredits) * 100}%`,
                      minWidth: "30px",
                      height: "40px",
                      backgroundColor: theme.palette.primary.main,
                      opacity: 0.7 + index * 0.05,
                      borderRadius: "4px",
                      mr: 0.5,
                      position: "relative",
                      overflow: "hidden",
                      "&:hover": {
                        opacity: 1,
                        transform: "translateY(-4px)",
                        transition: "all 0.3s ease",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        position: "absolute",
                        bottom: 0,
                        left: 0,
                        width: "100%",
                        textAlign: "center",
                        color: "white",
                        fontSize: "0.75rem",
                        fontWeight: "bold",
                        padding: "2px",
                        textShadow: "0 1px 2px rgba(0,0,0,0.3)",
                      }}
                    >
                      {Math.round((customer.credits / totalCredits) * 100)}%
                    </Box>
                  </Box>
                </Tooltip>
              ))}
              {credits.length > 5 && (
                <Tooltip title="Other customers">
                  <Box
                    component={motion.div}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.3, delay: 0.5 }}
                    sx={{
                      width: "40px",
                      height: "40px",
                      backgroundColor: theme.palette.grey[400],
                      borderRadius: "4px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "white",
                      fontWeight: "bold",
                    }}
                  >
                    +{credits.length - 5}
                  </Box>
                </Tooltip>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Search and Filter */}
      <Paper
        component={motion.div}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        sx={{
          p: 2,
          mb: 3,
          borderRadius: 3,
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
          border: "1px solid rgba(0, 0, 0, 0.05)",
          background: "linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(249, 250, 251, 0.95) 100%)",
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search customers..."
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />,
                sx: {
                  borderRadius: 2,
                  backgroundColor: "background.paper",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgba(0, 0, 0, 0.1)",
                  },
                },
              }}
              sx={{ backgroundColor: "background.paper" }}
            />
          </Grid>
          <Grid item xs={12} md={6} sx={{ display: "flex", justifyContent: { xs: "flex-start", md: "flex-end" } }}>
            <Button
              variant="outlined"
              startIcon={<FilterIcon />}
              sx={{
                borderRadius: 2,
                mr: 1,
                borderColor: "rgba(0, 0, 0, 0.1)",
                color: "text.primary",
              }}
            >
              Filter
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate("/customers")}
              sx={{
                borderRadius: 2,
                boxShadow: "0 4px 14px 0 rgba(99, 102, 241, 0.39)",
                "&:hover": {
                  boxShadow: "0 6px 20px rgba(99, 102, 241, 0.5)",
                },
              }}
            >
              Add Customer
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Credits Table */}
      <AnimatePresence mode="wait">
        {credits.length === 0 ? (
          loading ? (
            <LoadingSpinner message="Loading credits data..." />
          ) : (
            <EmptyState
              title="No Credits Data"
              description="You haven't added any credits yet. Add credits to your customers to get started."
              icon={<CreditsIcon sx={{ fontSize: 60 }} />}
              actionText="Add Customer"
              actionIcon={<AddIcon />}
              onActionClick={() => navigate("/customers/create")}
            />
          )
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <Box id="credits-table-scrollable" sx={{ height: 600, overflow: 'auto' }}>
              <InfiniteScroll
                dataLength={credits.length}
                next={fetchNextPage}
                hasMore={!showLoadMore}
                loader={isFetching ? (<Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}><CircularProgress /></Box>) : null}
                endMessage={
                  showLoadMore && (
                    <Box sx={{ textAlign: 'center', my: 2, color: 'text.secondary' }}>
                      <b>No more credits to load.</b>
                      <Box sx={{ mt: 2 }}>
                        <button onClick={handleLoadMore} style={{ padding: '8px 16px', borderRadius: 4, border: 'none', background: '#1976d2', color: '#fff', cursor: 'pointer' }}>
                          Load More
                        </button>
                      </Box>
                    </Box>
                  )
                }
                scrollableTarget="credits-table-scrollable"
                scrollThreshold={0.98}
              >
                <TableContainer
                  component={Paper}
                  sx={{
                    borderRadius: 3,
                    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
                    border: "1px solid rgba(0, 0, 0, 0.05)",
                    overflow: "hidden",
                    mb: 3,
                  }}
                >
                  <Table>
                    <TableHead sx={{ backgroundColor: "rgba(99, 102, 241, 0.04)" }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: "bold" }}>Customer</TableCell>
                        <TableCell sx={{ fontWeight: "bold" }}>Email</TableCell>
                        <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: "bold" }} align="right">
                          Available Credits
                        </TableCell>
                        <TableCell sx={{ fontWeight: "bold" }} align="right">
                          Last Updated
                        </TableCell>
                        <TableCell sx={{ fontWeight: "bold" }} align="center">
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {credits.map((customer) => (
                        <TableRow
                          key={customer._id}
                          component={motion.tr}
                          whileHover={{ backgroundColor: "rgba(99, 102, 241, 0.04)" }}
                          sx={{
                            "&:last-child td, &:last-child th": { border: 0 },
                            cursor: "pointer",
                            transition: "all 0.2s",
                          }}
                          onClick={() => handleViewCustomerCredits(customer._id)}
                        >
                          <TableCell>
                            <Typography variant="body1" fontWeight={500}>
                              {customer.name}
                            </Typography>
                          </TableCell>
                          <TableCell>{customer.email || "—"}</TableCell>
                          <TableCell>
                            <StatusBadge status={customer.status || "active"} />
                          </TableCell>
                          <TableCell align="right">
                            <Typography
                              variant="body1"
                              fontWeight={600}
                              sx={{ color: customer.credits > 0 ? "success.main" : "error.main" }}
                            >
                              {customer.credits ? customer.credits.toLocaleString() : '0'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            {customer.lastUpdated ? new Date(customer.lastUpdated).toLocaleDateString() : "—"}
                          </TableCell>
                          <TableCell align="center">
                            <Box sx={{ display: "flex", justifyContent: "center" }}>
                              <Tooltip title="View Credits">
                                <IconButton
                                  color="primary"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleViewCustomerCredits(customer._id)
                                  }}
                                  sx={{
                                    "&:hover": {
                                      backgroundColor: "rgba(99, 102, 241, 0.1)",
                                    },
                                  }}
                                >
                                  <VisibilityIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Add Credits">
                                <IconButton
                                  color="primary"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleOpenAddCreditsDialog(customer)
                                  }}
                                  sx={{
                                    "&:hover": {
                                      backgroundColor: "rgba(99, 102, 241, 0.1)",
                                    },
                                  }}
                                >
                                  <AddIcon />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </InfiniteScroll>
            </Box>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add Credits Dialog */}
      <Dialog
        open={openAddCreditsDialog}
        onClose={handleCloseAddCreditsDialog}
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: "0 10px 40px rgba(0, 0, 0, 0.1)",
            width: "100%",
            maxWidth: "500px",
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h5" fontWeight={600}>
            Add Credits
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Add credits to {selectedCustomer?.name}
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Number of Credits"
            type="number"
            fullWidth
            variant="outlined"
            value={creditsToAdd}
            onChange={(e) => setCreditsToAdd(e.target.value)}
            InputProps={{
              inputProps: { min: 1 },
              sx: {
                borderRadius: 2,
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleCloseAddCreditsDialog} variant="outlined" sx={{ borderRadius: 2 }}>
            Cancel
          </Button>
          <LoadingButton
            onClick={handleAddCredits}
            loading={addingCredits}
            variant="contained"
            sx={{
              borderRadius: 2,
              boxShadow: "0 4px 14px 0 rgba(99, 102, 241, 0.39)",
              "&:hover": {
                boxShadow: "0 6px 20px rgba(99, 102, 241, 0.5)",
              },
            }}
          >
            Add Credits
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default Credits

